/**
 * The touch-probe cursor readout bubble — ported from `combo_probe.dart`. A
 * white map-anchored card with a downward tail at the tapped point. The header
 * shows the layer value + unit; the body shows lat/lon, wind-at-point, and a
 * "compare models ›" affordance. The host keeps `anchor` fresh on pan/zoom.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { ComboService, ComboState, defaultComboState } from './combo.service';
import { stateCfg } from './combo.service';
import { ComboLayer, fmtDispPartsForUser, unitLabelForUser } from './combo.models';
import { cardinal, WindReading } from './combo-wind-math';
import { UserSettingsService } from '../../../../services/user-settings.service';
import { speedFromMs } from '../../../../shared/utils/unit-formatter.util';

@Component({
  selector: 'app-combo-cursor-bubble',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './combo-cursor-bubble.component.html',
  styleUrl: './combo-cursor-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboCursorBubbleComponent implements OnInit, OnDestroy {
  /** Screen-space position of the probe point (px from the map's top-left). */
  @Input() anchor: { x: number; y: number } = { x: 0, y: 0 };
  /** Minimum left-edge margin so the bubble never collides with the opacity card. */
  @Input() leftMargin = 80;

  @Output() closeBubble = new EventEmitter<void>();
  @Output() openModels = new EventEmitter<void>();

  readonly inkSoft = '#6B7785';
  readonly maxCardWidth = 240;

  state: ComboState = defaultComboState();
  private sub?: Subscription;

  constructor(
    public combo: ComboService,
    private cdr: ChangeDetectorRef,
    private userSettings: UserSettingsService,
  ) {}

  ngOnInit(): void {
    const sub = this.combo.state$.subscribe((s) => {
      this.state = s;
      this.cdr.markForCheck();
    });
    // The probe readout is one of the places that ignored the viewer's unit
    // preferences entirely — SST always read °F, wind/currents always kn.
    this.userSettings.ensureLoaded();
    sub.add(this.userSettings.settings$.subscribe(() => this.cdr.markForCheck()));
    this.sub = sub;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get visible(): boolean {
    return this.state.config.enabled && this.state.probe != null;
  }
  get cfg() {
    return stateCfg(this.state);
  }
  get accent(): string {
    return this.cfg.accent;
  }
  get isWind(): boolean {
    return this.state.config.layer === 'wind';
  }

  get probeLat(): number {
    return this.state.probe?.lat ?? 0;
  }
  get probeLon(): number {
    return this.state.probe?.lon ?? 0;
  }
  get latLonLabel(): string {
    return `${this.probeLat.toFixed(2)}, ${this.probeLon.toFixed(2)}`;
  }

  get wind(): WindReading | null {
    const probe = this.state.probe;
    if (probe == null) return null;
    if (this.isWind) {
      return this.state.windField?.sampleAt(this.state.windFrameIndex, probe.lat, probe.lon) ?? null;
    }
    return this.state.windGrid?.sample(probe.lat, probe.lon) ?? null;
  }

  get hasValue(): boolean {
    return this.isWind ? this.wind != null : this.state.probe?.value != null;
  }

  /** Big number in the header — value only; `unitText` renders the label. */
  get valueText(): string {
    if (!this.hasValue) return '—';
    // Wind speed arrives in knots; every layer converts through the same helper
    // so the number and the label below it can never disagree.
    const native = this.isWind ? this.wind!.spd : this.state.probe!.value!;
    const decimals = this.isWind ? 0 : undefined;
    return fmtDispPartsForUser(this.cfg, native, this.userSettings.snapshot, decimals).value;
  }

  /** Unit caption beside the value, in the viewer's preferred unit. */
  get unitText(): string {
    return unitLabelForUser(this.cfg, this.userSettings.snapshot);
  }

  get windSpeedText(): string {
    const w = this.wind;
    if (w == null) return '—  ';
    // Native knots → the viewer's speed unit (this line hard-coded "kn").
    return speedFromMs(w.spd * 0.514444, this.userSettings.speedUnit, 0).text;
  }
  get windDirText(): string {
    const w = this.wind;
    return w != null ? `from ${cardinal(w.dir)}` : 'wind';
  }
  /** Arrow rotation in degrees — points downwind (dir+180). */
  get windArrowDeg(): number {
    const w = this.wind;
    return w != null ? (w.dir + 180) % 360 : 0;
  }
  get windOpacity(): number {
    return this.wind != null ? 1 : 0.25;
  }

  get modelsEnabled(): boolean {
    return this.combo.caps.wind;
  }

  layerIconKey(l: ComboLayer): string {
    return l;
  }

  // ── Positioning: card sits above the anchor with a downward tail ──────────
  get cardLeft(): number {
    return Math.max(this.leftMargin, this.anchor.x);
  }
  get cardTop(): number {
    return this.anchor.y;
  }

  onClose(): void {
    this.closeBubble.emit();
  }
  onOpenModels(): void {
    this.openModels.emit();
  }
}
